import React, { useContext, useMemo } from 'react';
import { ThemeContext } from 'grommet';
import { normalizeColor } from 'grommet/utils';
import { Bar, BarChart, Cell, Rectangle, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import styled from 'styled-components';
import { monthName, monthTicks, quarterTicks, ratePercent, weekLabel } from '../../utils/chart-data';
import { formatCurrencySI } from '../../utils/numbers';
import ChartTooltip from './ChartTooltip';

const HATCH_ID = 'collections-estimate';

export const HatchSwatch = styled.span`
    width: 10px; height: 10px; border-radius: 2px; flex: none;
    background: repeating-linear-gradient(45deg, ${(props) => props.$color} 0 2px, transparent 2px 4px);
`;

// On a plate, because the line runs across the bars and bare text would sit on top of them
const UsualLabel = ({ viewBox, value, fill, background }) => (
    <g>
        <rect x={viewBox.x} y={viewBox.y - 24} width={value.length * 7.6 + 12} height={22} rx={4} fill={background} />
        <text x={viewBox.x + 6} y={viewBox.y - 8} fill={fill} fontSize={14}>{value}</text>
    </g>
);

// A bar, plus a break across its top when it is `clipped`: its rate is too far above the other
// weeks to draw to scale, so it stops at the top of the chart. The break is cut in the chart's
// background color, the way an axis break is drawn.
export const CollectionBar = ({ gap, ...props }) => {
    const { x, y, width, height, payload } = props;
    return (
        <g>
            <Rectangle {...props} />
            {payload?.clipped && height > 12 && (
                <line
                    data-testid="clipped-mark"
                    x1={x - 1}
                    y1={y + 9}
                    x2={x + width + 1}
                    y2={y + 5}
                    stroke={gap}
                    strokeWidth={2.5}
                />
            )}
        </g>
    );
};

// `selected` is the week_start the split below follows. `lit` adds every real week, for early
// merchants, where a lone highlighted bar among placeholders would read as the only data.
const CollectionBars = ({ bars, top, usualRate, currency, selected, lit, onHover, onSelect }) => {
    const theme = useContext(ThemeContext);
    const color = (name) => normalizeColor(name, theme);
    const primary = color('barchart-primary');
    const muted = color('chart-muted');
    const ticks = useMemo(() => (bars.length > 26 ? quarterTicks(bars) : monthTicks(bars)), [bars]);
    const money = (amount) => formatCurrencySI(amount, currency);
    const barAt = (state) => bars[state?.activeTooltipIndex];

    const renderTooltip = ({ active, payload }) => {
        const bar = payload?.[0]?.payload;
        if (!active || !bar || bar.stub) return null;
        const opening = `of ${money(bar.opening_receivables)} receivables`;
        if (bar.isEstimate) {
            return <ChartTooltip title="This week, estimated" lines={[`${money(bar.payments)} expected`, `${ratePercent(bar.rate)} ${opening}`]} />;
        }
        return (
            <ChartTooltip
                title={`Week of ${weekLabel(bar.week_start)}`}
                lines={[
                    `${money(bar.payments)} collected`,
                    ...(bar.rate != null ? [`${ratePercent(bar.rate)} ${opening}`] : []),
                    ...(bar.clipped ? ['Too high to draw to scale'] : []),
                ]}
            />
        );
    };

    const fill = (bar) => {
        if (bar.isEstimate) return `url(#${HATCH_ID})`;
        if (bar.stub) return muted;
        return bar.week_start === selected || lit ? primary : muted;
    };

    return (
        <ResponsiveContainer width="100%" height={190}>
            <BarChart
                data={bars}
                barCategoryGap="24%"
                margin={{ top: 4, right: 0, left: 0, bottom: 0 }}
                onMouseMove={(state) => onHover(barAt(state))}
                onMouseLeave={() => onHover(null)}
                onClick={(state) => onSelect(barAt(state))}
            >
                <defs>
                    <pattern id={HATCH_ID} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                        <rect width="2" height="5" fill={primary} />
                    </pattern>
                </defs>
                <XAxis
                    dataKey="week_start"
                    ticks={ticks}
                    interval={0}
                    tickFormatter={monthName}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 16, fill: color('text-weak') }}
                />
                <YAxis hide domain={[0, top]} />
                <Tooltip cursor={false} isAnimationActive={false} content={renderTooltip} />
                <Bar
                    dataKey="value"
                    radius={[3, 3, 0, 0]}
                    maxBarSize={14}
                    minPointSize={3}
                    isAnimationActive={false}
                    shape={(props) => <CollectionBar {...props} gap={color('background')} />}
                >
                    {bars.map((bar) => <Cell key={bar.week_start} fill={fill(bar)} style={{ cursor: bar.stub || bar.isEstimate ? 'default' : 'pointer' }} />)}
                </Bar>
                {usualRate != null && (
                    <ReferenceLine
                        y={usualRate}
                        stroke={color('text-weak')}
                        strokeDasharray="4 4"
                        label={<UsualLabel value={`Usual ${ratePercent(usualRate)}`} fill={color('text-weak')} background={color('background')} />}
                    />
                )}
            </BarChart>
        </ResponsiveContainer>
    );
};

export default CollectionBars;
